import React, { useState, useEffect } from 'react';
import { getColours, createColour, updateColour, deleteColour, getStockMovements, deleteStockMovement, adjustStock } from '../../lib/treeApi';
import { useTreeAuth } from '../../lib/treeAuth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, Package, ArrowUp, ArrowDown, Clock, Pencil, Check, X, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { COLOR_PALETTE, swatchStyle, registerColours } from '../../lib/treeColors';

export default function TreePalletsPage() {
  const { settings } = useTreeAuth();
  const [colours, setColours] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddColour, setShowAddColour] = useState(false);
  const [newColour, setNewColour] = useState({ color: '', label: '', swatch: '' });
  const [editingColour, setEditingColour] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [swatchEditTarget, setSwatchEditTarget] = useState(null);
  const [swatchEditLabel, setSwatchEditLabel] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [colRes, movRes] = await Promise.all([getColours(), getStockMovements({ limit: 20 })]);
      setColours(colRes.data);
      registerColours(colRes.data);
      setMovements(movRes.data);
    } catch { toast.error('Failed to fetch'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleAddColour = async () => {
    if (!newColour.color.trim()) return toast.error('Enter a colour name');
    try {
      await createColour(newColour);
      toast.success('Colour added');
      setShowAddColour(false);
      setNewColour({ color: '', label: '', swatch: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteColour = async (id, name) => {
    if (!window.confirm(`Delete colour "${name}"? This cannot be undone.`)) return;
    try { await deleteColour(id); toast.success('Colour removed'); fetchData(); } catch { toast.error('Failed'); }
  };

  const openSwatchEdit = (c) => { setSwatchEditTarget(c); setSwatchEditLabel(c.label || c.color); };

  const handleSaveSwatch = async (swatchKey) => {
    try {
      await updateColour(swatchEditTarget.id, { swatch: swatchKey, label: swatchEditLabel });
      toast.success('Colour updated');
      setSwatchEditTarget(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDeleteMovement = async (id) => {
    try {
      const { data } = await deleteStockMovement(id);
      toast.success(`Removed — ${data.color} stock corrected to ${data.quantity}`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const startEditStock = (c) => { setEditingColour(c.color); setEditValue(String(c.quantity || 0)); };
  const cancelEditStock = () => { setEditingColour(null); setEditValue(''); };

  const handleSaveStock = async (c) => {
    const target = parseInt(editValue, 10);
    if (Number.isNaN(target)) return toast.error('Enter a whole number');
    const change = target - (c.quantity || 0);
    if (change === 0) return cancelEditStock();
    setSavingEdit(true);
    try {
      await adjustStock({ color: c.color, change, notes: 'Manual stock correction' });
      toast.success(`${c.label || c.color} stock set to ${target}`);
      cancelEditStock();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    setSavingEdit(false);
  };

  const totalStock = colours.reduce((sum, c) => sum + (c.quantity || 0), 0);

  return (
    <div className="space-y-6" data-testid="tree-stock-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1711] font-['Outfit'] tracking-tight">Stock</h1>
          <p className="text-sm text-[#4A5D53] mt-1">Current stock levels by colour</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white border border-[#1A4331]/15 rounded-xl px-4 py-2">
            <span className="text-xs uppercase tracking-wider text-[#4A5D53] font-bold">Total</span>
            <p className="text-2xl font-bold text-[#0A1711] font-['Outfit']">{totalStock} <span className="text-sm font-normal text-[#4A5D53]">pallets</span></p>
            <p className="text-xs text-[#4A5D53]">{(totalStock * settings.trees_per_pallet).toLocaleString()} trees</p>
          </div>
          <Dialog open={showAddColour} onOpenChange={setShowAddColour}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A4331] text-white hover:bg-[#1A4331]/90 rounded-lg" data-testid="tree-add-colour-button">
                <Plus className="w-4 h-4 mr-2" /> Add Colour
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="tree-add-colour-dialog">
              <DialogHeader><DialogTitle className="font-['Outfit']">Add Colour Code</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Pick a colour</Label>
                  <div className="grid grid-cols-4 gap-2 mt-1.5" data-testid="tree-colour-palette">
                    {COLOR_PALETTE.map((sw) => {
                      const taken = colours.some(c => c.color === sw.color);
                      const selected = newColour.color === sw.color;
                      return (
                        <button
                          key={sw.color}
                          type="button"
                          disabled={taken}
                          onClick={() => setNewColour({ color: sw.color, label: sw.label })}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${selected ? 'border-[#1A4331] bg-[#1A4331]/5' : 'border-transparent hover:border-[#1A4331]/20'} ${taken ? 'opacity-30 cursor-not-allowed' : ''}`}
                          data-testid={`tree-swatch-${sw.color.replace(/\s+/g, '-')}`}
                        >
                          <div className="w-8 h-8 rounded-full" style={swatchStyle(sw.color)} />
                          <span className="text-[10px] text-[#4A5D53] font-medium leading-tight text-center">{sw.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label>Colour Name</Label>
                  <Input value={newColour.color} onChange={e => setNewColour({...newColour, color: e.target.value})} placeholder="e.g. green, or pick a swatch above" data-testid="tree-colour-name-input" />
                </div>
                <div>
                  <Label>Display Label (optional)</Label>
                  <Input value={newColour.label} onChange={e => setNewColour({...newColour, label: e.target.value})} placeholder="e.g. Nordmann Green" />
                </div>
                <Button onClick={handleAddColour} className="w-full bg-[#1A4331] text-white" data-testid="tree-submit-colour-button">Add Colour</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Dialog open={!!swatchEditTarget} onOpenChange={(open) => !open && setSwatchEditTarget(null)}>
        <DialogContent data-testid="tree-edit-swatch-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit Colour — {swatchEditTarget?.color}</DialogTitle></DialogHeader>
          {swatchEditTarget && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Display Label</Label>
                <Input value={swatchEditLabel} onChange={e => setSwatchEditLabel(e.target.value)} data-testid="tree-edit-swatch-label-input" />
              </div>
              <div>
                <Label>Swatch</Label>
                <div className="grid grid-cols-4 gap-2 mt-1.5" data-testid="tree-edit-swatch-palette">
                  {COLOR_PALETTE.map((sw) => {
                    const selected = (swatchEditTarget.swatch || '').toLowerCase() === sw.color;
                    return (
                      <button
                        key={sw.color}
                        type="button"
                        onClick={() => handleSaveSwatch(sw.color)}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${selected ? 'border-[#1A4331] bg-[#1A4331]/5' : 'border-transparent hover:border-[#1A4331]/20'}`}
                        data-testid={`tree-edit-swatch-option-${sw.color.replace(/\s+/g, '-')}`}
                      >
                        <div className="w-8 h-8 rounded-full relative" style={swatchStyle(sw.color)}>
                          {selected && <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow" />}
                        </div>
                        <span className="text-[10px] text-[#4A5D53] font-medium leading-tight text-center">{sw.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-xs text-[#4A5D53]">Picking a swatch saves instantly. Changing just the label? Hit save below.</p>
              <Button onClick={() => handleSaveSwatch(swatchEditTarget.swatch)} variant="outline" className="w-full" data-testid="tree-save-label-only-button">Save Label Only</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tree-colour-cards">
        {colours.map((c) => (
          <div key={c.id} className="bg-white border border-[#1A4331]/15 rounded-xl p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid={`tree-colour-card-${c.color}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => openSwatchEdit(c)} className="w-10 h-10 rounded-lg flex items-center justify-center relative group" style={swatchStyle(c.color)} title="Edit colour swatch" data-testid={`tree-edit-swatch-${c.color}`}>
                  <Package className="w-5 h-5 text-white drop-shadow group-hover:opacity-0 transition-opacity" />
                  <Palette className="w-4 h-4 text-white drop-shadow absolute opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <div>
                  <h3 className="font-semibold text-[#0A1711] font-['Outfit'] capitalize">{c.label || c.color}</h3>
                  <p className="text-[10px] text-[#4A5D53] uppercase tracking-wider">{c.color}</p>
                </div>
              </div>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="sm" onClick={() => startEditStock(c)} className="text-[#4A5D53] hover:text-[#1A4331]" title="Set exact stock quantity" data-testid={`tree-edit-stock-${c.color}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteColour(c.id, c.label || c.color)} className="text-red-400 hover:text-red-600" data-testid={`tree-delete-colour-${c.color}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {editingColour === c.color ? (
              <div className="flex items-center gap-2" data-testid={`tree-stock-edit-row-${c.color}`}>
                <Input
                  type="number"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveStock(c); if (e.key === 'Escape') cancelEditStock(); }}
                  autoFocus
                  className="text-2xl font-bold font-['Outfit'] h-12"
                  data-testid={`tree-stock-edit-input-${c.color}`}
                />
                <Button size="sm" onClick={() => handleSaveStock(c)} disabled={savingEdit} className="bg-emerald-600 text-white h-9 w-9 p-0" data-testid={`tree-stock-edit-save-${c.color}`}>
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={cancelEditStock} className="h-9 w-9 p-0" data-testid={`tree-stock-edit-cancel-${c.color}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <p className="text-4xl font-bold font-['Outfit'] mb-1" style={{ color: (c.quantity || 0) < 0 ? '#DC2626' : '#0A1711' }}>{c.quantity || 0}</p>
                <p className="text-xs text-[#4A5D53]">{(c.quantity || 0) * settings.trees_per_pallet} trees</p>
              </>
            )}
          </div>
        ))}
        {colours.length === 0 && !loading && (
          <div className="col-span-full flex flex-col items-center py-12 text-[#4A5D53]">
            <Package className="w-8 h-8 mb-2 opacity-40" />
            <p>No colours set up yet. Add your first colour code.</p>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#1A4331]/15 rounded-xl overflow-hidden" data-testid="tree-movements-table">
        <div className="px-6 py-4 border-b border-[#1A4331]/10">
          <h3 className="text-lg font-semibold text-[#0A1711] font-['Outfit']">Recent Activity</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F1ED]/50">
              <TableHead className="font-semibold">Time</TableHead>
              <TableHead className="font-semibold">Colour</TableHead>
              <TableHead className="font-semibold">Change</TableHead>
              <TableHead className="font-semibold">Notes</TableHead>
              <TableHead className="font-semibold">By</TableHead>
              <TableHead className="font-semibold w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movements.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#4A5D53]">No activity yet.</TableCell></TableRow>
            ) : (
              movements.map((m) => (
                <TableRow key={m.id} className="hover:bg-[#F4F1ED]/30">
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1"><Clock className="w-3 h-3 text-[#4A5D53]" />{new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-semibold capitalize bg-[#F4F1ED] text-[#0A1711]">
                      <div className="w-2 h-2 rounded-full" style={swatchStyle(m.color)} />
                      {colours.find(c => c.color === m.color)?.label || m.color}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`flex items-center gap-1 font-bold text-sm ${m.change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {m.change > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      {m.change > 0 ? '+' : ''}{m.change}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-[#4A5D53] max-w-[200px] truncate">{m.notes}</TableCell>
                  <TableCell className="text-xs text-[#4A5D53]">{m.user_name}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteMovement(m.id)} className="text-red-400 hover:text-red-600" title="Remove this entry and correct the stock total" data-testid={`tree-delete-movement-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
